// Card Type Constants
export enum CardType {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  JCB = 'JCB',
  UNIONPAY = 'UNIONPAY',
  GPN = 'GPN',
  AMERICAN_EXPRESS = 'AMERICAN_EXPRESS',
  DISCOVER = 'DISCOVER',
  DINERS_CLUB = 'DINERS_CLUB',
  MAESTRO = 'MAESTRO',
  UNKNOWN = 'UNKNOWN',
  INVALID = 'INVALID',
}

// Interface for card detection result
export interface CardInfo {
  type: CardType;
  isValid: boolean;
  isLuhnValid: boolean;
  cardNumber: string;
  description: string;
}

/**
 * Main function to detect card type and validate
 * @param cardNumber - Card number with or without spaces/dashes
 * @returns Card information object
 */
export function detectCardType(cardNumber: string): CardInfo {
  // Clean the card number (remove spaces, dashes, etc.)
  const cleanNumber = cleanCardNumber(cardNumber);

  // Basic validation
  if (cleanNumber.length < 12 || cleanNumber.length > 19) {
    return {
      type: CardType.INVALID,
      isValid: false,
      isLuhnValid: false,
      cardNumber: cleanNumber,
      description: 'Invalid card length',
    };
  }

  // Check if all characters are digits
  if (!/^\d+$/.test(cleanNumber)) {
    return {
      type: CardType.INVALID,
      isValid: false,
      isLuhnValid: false,
      cardNumber: cleanNumber,
      description: 'Contains non-digit characters',
    };
  }

  // Validate using Luhn algorithm
  const luhnValid = validateLuhn(cleanNumber);

  // Detect card type
  const { type, description } = identifyCardType(cleanNumber);

  // Check if length is valid for the detected card type
  const lengthValid = isValidLengthForType(cleanNumber, type);

  return {
    type,
    isValid: lengthValid && luhnValid,
    isLuhnValid: luhnValid,
    cardNumber: cleanNumber,
    description,
  };
}

/**
 * Remove all non-digit characters from card number
 * @param cardNumber - Card number string
 * @returns Clean card number
 */
function cleanCardNumber(cardNumber: string): string {
  return cardNumber.replace(/\D/g, '');
}

/**
 * Validate card number using Luhn algorithm
 * @param cardNumber - Clean card number string
 * @returns True if valid
 */
export function validateLuhn(cardNumber: string): boolean {
  let sum = 0;
  let isEven = false;

  // Process digits from right to left
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

/**
 * Identify card type based on prefix patterns
 * @param cardNumber - Clean card number string
 * @returns Object with type and description
 */
function identifyCardType(cardNumber: string): {
  type: CardType;
  description: string;
} {
  // Check for GPN (Indonesian National Payment Gateway)
  if (isGPNPattern(cardNumber)) {
    return {
      type: CardType.GPN,
      description: 'Indonesian National Payment Gateway',
    };
  }

  // Visa: starts with 4
  if (cardNumber.startsWith('4')) {
    return {
      type: CardType.VISA,
      description: 'Visa card',
    };
  }

  // MasterCard: 51-55 or 2221-2720
  // if (matchesMasterCardPattern(cardNumber)) {
  //   return {
  //     type: CardType.MASTERCARD,
  //     description: 'MasterCard',
  //   };
  // }

  // American Express: 34 or 37
  // if (cardNumber.startsWith('34') || cardNumber.startsWith('37')) {
  //   return {
  //     type: CardType.AMERICAN_EXPRESS,
  //     description: 'American Express',
  //   };
  // }

  // JCB: 35 (16 digits) or 2131/1800 (15 digits)
  // if (matchesJCBPattern(cardNumber)) {
  //   return {
  //     type: CardType.JCB,
  //     description: 'Japan Credit Bureau',
  //   };
  // }

  // UnionPay: 62 (and specific ranges)
  // if (matchesUnionPayPattern(cardNumber)) {
  //   return {
  //     type: CardType.UNIONPAY,
  //     description: 'China UnionPay',
  //   };
  // }

  // Discover: 6011, 622126-622925, 644-649, 65
  // if (matchesDiscoverPattern(cardNumber)) {
  //   return {
  //     type: CardType.DISCOVER,
  //     description: 'Discover Card',
  //   };
  // }

  // Diners Club: 30, 36, 38
  // if (matchesDinersClubPattern(cardNumber)) {
  //   return {
  //     type: CardType.DINERS_CLUB,
  //     description: 'Diners Club',
  //   };
  // }

  // Maestro: 50, 56-58, 6390, 67
  // if (matchesMaestroPattern(cardNumber)) {
  //   return {
  //     type: CardType.MAESTRO,
  //     description: 'Maestro',
  //   };
  // }

  return {
    type: CardType.UNKNOWN,
    description: 'Unknown card type',
  };
}

/**
 * Check if card matches MasterCard patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches MasterCard pattern
 */
// function matchesMasterCardPattern(cardNumber: string): boolean {
//   if (cardNumber.length < 4) return false;

//   // Legacy range: 51-55
//   const first2 = cardNumber.substring(0, 2);
//   if (first2 >= '51' && first2 <= '55') {
//     return true;
//   }

//   // New range: 2221-2720
//   const first4 = cardNumber.substring(0, 4);
//   if (first4 >= '2221' && first4 <= '2720') {
//     return true;
//   }

//   return false;
// }

/**
 * Check if card matches JCB patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches JCB pattern
 */
// function matchesJCBPattern(cardNumber: string): boolean {
//   return (
//     cardNumber.startsWith('35') ||
//     cardNumber.startsWith('2131') ||
//     cardNumber.startsWith('1800')
//   );
// }

/**
 * Check if card matches UnionPay patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches UnionPay pattern
 */
// function matchesUnionPayPattern(cardNumber: string): boolean {
//   if (cardNumber.length < 3) return false;

//   // Primary range: 62
//   if (cardNumber.startsWith('62')) {
//     return true;
//   }

//   // Extended ranges: 620-625
//   if (cardNumber.length >= 3) {
//     const first3 = cardNumber.substring(0, 3);
//     if (first3 >= '620' && first3 <= '625') {
//       return true;
//     }
//   }

//   return false;
// }

/**
 * Check if card matches Discover patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches Discover pattern
 */
// function matchesDiscoverPattern(cardNumber: string): boolean {
//   if (cardNumber.startsWith('6011') || cardNumber.startsWith('65')) {
//     return true;
//   }

//   // Range 622126-622925 (some overlap with UnionPay)
//   if (cardNumber.length >= 6) {
//     const first6 = cardNumber.substring(0, 6);
//     if (first6 >= '622126' && first6 <= '622925') {
//       return true;
//     }
//   }

//   // Range 644-649
//   if (cardNumber.length >= 3) {
//     const first3 = cardNumber.substring(0, 3);
//     if (first3 >= '644' && first3 <= '649') {
//       return true;
//     }
//   }

//   return false;
// }

/**
 * Check if card matches Diners Club patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches Diners Club pattern
 */
// function matchesDinersClubPattern(cardNumber: string): boolean {
//   return (
//     cardNumber.startsWith('30') ||
//     cardNumber.startsWith('36') ||
//     cardNumber.startsWith('38')
//   );
// }

/**
 * Check if card matches Maestro patterns
 * @param cardNumber - Clean card number string
 * @returns True if matches Maestro pattern
 */
// function matchesMaestroPattern(cardNumber: string): boolean {
//   if (cardNumber.startsWith('50')) {
//     return true;
//   }

//   if (cardNumber.length >= 2) {
//     const first2 = cardNumber.substring(0, 2);
//     if (first2 >= '56' && first2 <= '58') {
//       return true;
//     }
//   }

//   return cardNumber.startsWith('6390') || cardNumber.startsWith('67');
// }

/**
 * Check if card matches GPN patterns (Indonesian banks)
 * @param cardNumber - Clean card number string
 * @returns True if matches GPN pattern
 */
function isGPNPattern(cardNumber: string): boolean {
  // Some Indonesian bank BIN ranges (simplified examples)
  const indonesianBINs: string[] = [
    '4773', // BCA
    '4894', // BNI
    '4532', // Mandiri
    '5573', // BRI
    '6264', // CIMB Niaga
    '4899', // Danamon
    '5264', // Bank Mega
    '4059', // OCBC NISP
    '4761', // Permata Bank
    '4681', // BTN
    '6017', // TMRW
  ];

  if (cardNumber.length >= 4) {
    const first4 = cardNumber.substring(0, 4);
    return indonesianBINs.includes(first4);
  }

  return false;
}

/**
 * Check if card length is valid for the detected type
 * @param cardNumber - Clean card number string
 * @param cardType - Detected card type
 * @returns True if length is valid for the card type
 */
function isValidLengthForType(cardNumber: string, cardType: CardType): boolean {
  const length = cardNumber.length;

  switch (cardType) {
    case CardType.VISA:
      return length === 13 || length === 16 || length === 19;
    case CardType.MASTERCARD:
      return length === 16;
    case CardType.AMERICAN_EXPRESS:
      return length === 15;
    case CardType.JCB:
      return length === 15 || length === 16;
    case CardType.UNIONPAY:
      return length >= 16 && length <= 19;
    case CardType.DISCOVER:
      return length === 16;
    case CardType.DINERS_CLUB:
      return length === 14;
    case CardType.MAESTRO:
      return length >= 12 && length <= 19;
    case CardType.GPN:
      return length >= 13 && length <= 19;
    default:
      return length >= 12 && length <= 19;
  }
}

/**
 * Process multiple card numbers at once
 * @param cardNumbers - Array of card number strings
 * @returns Array of card info objects
 */
export function detectMultipleCards(cardNumbers: string[]): CardInfo[] {
  return cardNumbers.map((cardNumber) => detectCardType(cardNumber));
}

/**
 * Check if a card type is supported for processing
 * @param cardType - Card type to check
 * @param supportedTypes - Array of supported card types
 * @returns True if card type is supported
 */
export function isCardTypeSupported(
  cardType: CardType,
  supportedTypes: CardType[]
): boolean {
  return supportedTypes.includes(cardType);
}

/**
 * Get card type from string (case insensitive)
 * @param cardTypeStr - Card type string
 * @returns CardType enum value
 */
export function getCardTypeFromString(cardTypeStr: string): CardType {
  const upperStr = cardTypeStr.toUpperCase();
  return CardType[upperStr as keyof typeof CardType] || CardType.UNKNOWN;
}

// Example usage and testing
export function runTests(): void {
  console.log('Card Type Detection Results:');
  console.log('='.repeat(80));

  const testCards: string[] = [
    '4111 1111 1111 1111', // Visa
    '5555 5555 5555 4444', // MasterCard
    '3782 822463 10005', // American Express
    '3530 1113 3330 0000', // JCB
    '6212 3456 7890 1234', // UnionPay
    '6011 1111 1111 1117', // Discover
    '3056 9300 0902 0004', // Diners Club
    '6759 6498 2643 8453', // Maestro
    '4773 1234 5678 9012', // Potential GPN (BCA)
    '1234 5678 9012 3456', // Invalid
    '2223 0000 4841 0010', // MasterCard New Series
    '6264 1234 5678 9012', // Potential GPN (CIMB Niaga)
  ];

  testCards.forEach((card) => {
    const result = detectCardType(card);
    console.log(
      `Card: ${card.padEnd(20)} | Type: ${result.type.padEnd(18)} | Valid: ${result.isValid.toString().padEnd(5)} | Luhn: ${result.isLuhnValid.toString().padEnd(5)} | ${result.description}`
    );
  });

  // Test multiple cards detection
  console.log('\n--- Multiple Cards Detection ---');
  const multipleResults = detectMultipleCards([
    '4111111111111111',
    '5555555555554444',
  ]);
  multipleResults.forEach((result, index) => {
    console.log(
      `Card ${index + 1}: ${result.type} - ${result.isValid ? 'Valid' : 'Invalid'}`
    );
  });

  // Test supported card types
  console.log('\n--- Card Support Check ---');
  const supportedTypes: CardType[] = [
    CardType.VISA,
    CardType.MASTERCARD,
    CardType.GPN,
  ];
  console.log(
    `Visa supported: ${isCardTypeSupported(CardType.VISA, supportedTypes)}`
  );
  console.log(
    `AmEx supported: ${isCardTypeSupported(CardType.AMERICAN_EXPRESS, supportedTypes)}`
  );
}
